import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Calculate available show dates for a given month
export function calculateShowDates(
  startsAt: string | null,
  repeatSettings: {
    option: string;
    frequency?: 'daily' | 'weekly' | 'monthly';
    interval?: number;
    weekdays?: string[];
    endType?: 'never' | 'after' | 'on';
    endAfterOccurrences?: number;
    endOnDate?: string;
    monthDays?: number[];
  } | null,
  currentMonth: Date
): Date[] {
  if (!startsAt) return [];
  const startDate = new Date(startsAt);
  const dates: Date[] = [];

  // Helper to normalize date to YMD (midnight, UTC)
  function toUTCYMD(date: Date) {
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }

  if (!repeatSettings || repeatSettings.option === 'does_not_repeat') {
    if (startDate.getMonth() === currentMonth.getMonth() && startDate.getFullYear() === currentMonth.getFullYear()) {
      dates.push(startDate);
    }
    return dates;
  }

  const { option, frequency, interval = 1, weekdays, monthDays, endType, endAfterOccurrences, endOnDate } = repeatSettings;

  // For weekly repeats, start from the first occurrence of the first selected weekday in the month
  if ((option === 'weekly' || (option === 'custom' && frequency === 'weekly')) && weekdays && weekdays.length > 0) {
    // Convert weekday names to numbers (0-6)
    const weekdayNumbers = weekdays.map((day: string) => {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days.indexOf(day);
    });

    // Start from the beginning of the month
    let currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    
    // If we're in a month after the start date, we need to adjust for the interval
    if (currentMonth > startDate) {
      const weeksDiff = Math.floor((currentDate.getTime() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
      const adjustedWeeks = weeksDiff % interval;
      if (adjustedWeeks !== 0) {
        currentDate = new Date(currentDate.getTime() + (interval - adjustedWeeks) * 7 * 24 * 60 * 60 * 1000);
      }
    }

    while (toUTCYMD(currentDate) <= toUTCYMD(lastDay)) {
      const currentDay = currentDate.getDay();
      
      // Check if current day is one of the selected weekdays
      if (weekdayNumbers.includes(currentDay)) {
        // Only add dates that are on or after the start date
        if (currentDate >= startDate) {
          if (endType === 'on' && endOnDate && currentDate > new Date(endOnDate)) {
            break;
          }
          dates.push(new Date(currentDate));
        }
      }
      
      // Move to next day
      currentDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);
      
      // If we've completed a week, skip ahead based on the interval
      if (currentDay === 6) { // If it's Saturday
        currentDate = new Date(currentDate.getTime() + (interval - 1) * 7 * 24 * 60 * 60 * 1000);
      }
    }
    
    return dates;
  }

  // For monthly_days repeats, check if the current day of the month is in the selected days
  if ((option === 'monthly_days' || (option === 'custom' && frequency === 'monthly' && monthDays && monthDays.length > 0)) && monthDays && monthDays.length > 0) {
    // Start from the beginning of the month
    let currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    
    // If we're in a month after the start date, we need to adjust for the interval
    if (currentMonth > startDate) {
      const monthsDiff = (currentMonth.getFullYear() - startDate.getFullYear()) * 12 + 
                         (currentMonth.getMonth() - startDate.getMonth());
      const adjustedMonths = monthsDiff % interval;
      if (adjustedMonths !== 0) {
        // Skip to the next interval
        currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - adjustedMonths, 1);
      }
    }

    while (toUTCYMD(currentDate) <= toUTCYMD(lastDay)) {
      const dayOfMonth = currentDate.getDate();
      
      // Check if current day is one of the selected days
      if (monthDays.includes(dayOfMonth)) {
        // Only add dates that are on or after the start date
        if (currentDate >= startDate) {
          if (endType === 'on' && endOnDate && currentDate > new Date(endOnDate)) {
            break;
          }
          dates.push(new Date(currentDate));
        }
      }
      
      // Move to next day
      currentDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);
    }
    
    return dates;
  }

  // Handle other repeat types (daily, weekdays, weekends, monthly)
  const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  let currentDate = new Date(Math.max(
    new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getTime(),
    new Date(startDate).getTime()
  ));
  while (toUTCYMD(currentDate) <= toUTCYMD(lastDay)) {
    if (endType === 'on' && endOnDate && currentDate > new Date(endOnDate)) {
      break;
    }

    if (toUTCYMD(currentDate) >= toUTCYMD(startDate)) {
      switch (option) {
        case 'daily':
          dates.push(new Date(currentDate));
          break;
        case 'weekdays':
          if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
            dates.push(new Date(currentDate));
          }
          break;
        case 'weekends':
          if (currentDate.getDay() === 0 || currentDate.getDay() === 6) {
            dates.push(new Date(currentDate));
          }
          break;
        case 'monthly':
          dates.push(new Date(currentDate));
          break;
        default:
          break;
      }
    }
    // Always increment currentDate at the end of the loop
    switch (option) {
      case 'daily':
        currentDate = new Date(currentDate.getTime() + interval * 24 * 60 * 60 * 1000);
        break;
      case 'monthly':
        currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + interval, currentDate.getDate());
        break;
      default:
        currentDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);
        break;
    }
  }

  return dates;
}

// Helper function to check if we're in production
export function isProduction() {
  return process.env.NODE_ENV === 'production';
}

/** Local dev, or Vercel preview / Vercel "development" — not the production deployment. */
export function isNonProductionAppEnvironment() {
  if (process.env.NODE_ENV !== 'production') return true
  const vercel = process.env.NEXT_PUBLIC_VERCEL_ENV
  return vercel === 'preview' || vercel === 'development'
}

// Referral code helpers
export async function createReferralCodeForWorkspace(workspaceId: string): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('Creating referral code for workspace:', workspaceId);
    const response = await fetch('/api/stripe/create-referral-code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ workspaceId }),
    });

    console.log('Response status:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('API Error:', errorData);
      
      if (response.status === 401) {
        return { success: false, error: 'Authentication required - user must be logged in' };
      } else if (response.status === 403) {
        return { success: false, error: 'Permission denied - user must be a member of the workspace' };
      } else if (response.status === 404) {
        return { success: false, error: 'Workspace not found' };
      } else {
        return { success: false, error: errorData.error || `Failed to create referral code (${response.status})` };
      }
    }

    const result = await response.json();
    console.log('Referral code created successfully:', result);
    return { success: true };
  } catch (error) {
    console.error('Error creating referral code:', error);
    return { success: false, error: 'Network error creating referral code' };
  }
}
